/**
 * Live wiring health: edge + Railway fleet (for lobby/ops visibility).
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

type ReadyBody = {
  status?: string;
  ready?: boolean;
  checks?: {
    d1?: { ok?: boolean; ms?: number };
    fleetRailway?: { ok?: boolean; ms?: number; status?: number };
  };
};

export function ConnectionStatusBar() {
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const readyQ = useQuery({
    queryKey: ["api-ready"],
    queryFn: async (): Promise<ReadyBody> => {
      const r = await fetch("/api/ready", {
        headers: { Accept: "application/json" },
      });
      return r.json();
    },
    refetchInterval: 45_000,
    staleTime: 20_000,
    retry: 1,
  });

  const fleetOk = readyQ.data?.checks?.fleetRailway?.ok;
  const d1Ok = readyQ.data?.checks?.d1?.ok;
  const ready = readyQ.data?.ready === true;
  const loading = readyQ.isLoading && !readyQ.data;

  if (!online) {
    return (
      <div
        className="w-full px-3 py-1.5 text-center text-xs font-semibold"
        style={{
          background: "rgba(120,20,20,0.95)",
          color: "#fecaca",
          borderBottom: "1px solid rgba(255,80,80,0.35)",
        }}
        data-testid="connection-offline"
      >
        Offline — reconnect network to play
      </div>
    );
  }

  if (loading || ready) return null;

  return (
    <div
      className="w-full px-3 py-1.5 text-center text-xs"
      style={{
        background: "rgba(80,50,10,0.92)",
        color: "#fde68a",
        borderBottom: "1px solid rgba(234,179,8,0.3)",
      }}
      data-testid="connection-degraded"
    >
      Systems degraded
      {d1Ok === false ? " · edge ledger" : ""}
      {fleetOk === false ? " · Railway wallet API" : ""}
      {" · "}
      tables may still run on edge
    </div>
  );
}
